import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { 
  Mail, 
  Lock, 
  Eye, 
  EyeOff, 
  ArrowRight, 
  Building, 
  Users, 
  Shield,
  CheckCircle,
  GraduationCap,
  BookOpen,
  Award
} from 'lucide-react';

interface ConsultancyLoginFormData {
  email: string;
  password: string;
  consultancyCode?: string;
}

const ConsultancyLoginPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loginType, setLoginType] = useState<'consultant' | 'admin'>('consultant');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { register, handleSubmit, formState: { errors } } = useForm<ConsultancyLoginFormData>();

  const onSubmit = async (data: ConsultancyLoginFormData) => {
    try {
      setError('');
      setIsLoading(true);
      
      // Demo credentials for consultancy login
      if (loginType === 'consultant') {
        // Demo consultant credentials
        if ((data.email === 'consultant@sewa.com' && data.password === 'consultant123') ||
            (data.email === 'siva.karthi@sewa.com' && data.password === 'sivakarthi123') ||
            (data.email === 'manikandan@sewa.com' && data.password === 'manikandan123') ||
            (data.email === 'sathiyamoorthy@sewa.com' && data.password === 'dean123')) {
          
          localStorage.setItem('consultancyAuth', JSON.stringify({
            token: 'demo-consultant-token',
            user: { email: data.email, role: 'consultant' },
            loginType: 'consultant'
          }));
          
          navigate('/consultancy-dashboard');
          return;
        }
      } else if (loginType === 'admin') {
        // Admin credentials (same as main login)
        if (data.email === 'r.deepanrajofficial@gmail.com' && data.password === 'admin123') {
          localStorage.setItem('consultancyAuth', JSON.stringify({
            token: 'demo-admin-token',
            user: { email: data.email, role: 'admin' },
            loginType: 'admin'
          }));

          navigate('/admin-dashboard');
          return;
        }
      }
      
      // If demo credentials don't match, try backend API
      const response = await fetch('/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: data.email,
          password: data.password,
          consultancyCode: data.consultancyCode,
          loginType
        }),
      });

      if (response.ok) {
        const result = await response.json();
        
        // Store authentication token
        localStorage.setItem('consultancyAuth', JSON.stringify({
          token: result.token,
          user: result.user,
          loginType
        }));

        // Redirect based on login type
        if (loginType === 'admin') {
          navigate('/admin-dashboard');
        } else {
          navigate('/consultancy-dashboard');
        }
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Invalid credentials');
      }
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const consultancyFeatures = [
    {
      icon: <GraduationCap className="h-6 w-6" />,
      title: "Education Guidance",
      description: "Expert consultation for academic pathways"
    },
    {
      icon: <BookOpen className="h-6 w-6" />,
      title: "Course Selection",
      description: "Personalized course recommendations"
    },
    {
      icon: <Award className="h-6 w-6" />,
      title: "Scholarship Assistance",
      description: "Access to exclusive scholarship opportunities"
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: "Career Support",
      description: "Professional career development guidance"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-black flex items-center justify-center py-12 px-4 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Floating Orbs */}
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-gradient-to-r from-blue-400/20 to-purple-400/20 blur-xl"
            style={{
              width: Math.random() * 300 + 100,
              height: Math.random() * 300 + 100,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              x: [0, Math.random() * 100 - 50],
              y: [0, Math.random() * 100 - 50],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: Math.random() * 10 + 5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: Math.random() * 5,
            }}
          />
        ))}
      </div>

      <div className="max-w-6xl w-full relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Side - Branding & Features */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center lg:text-left"
          >
            {/* Logo & Branding */}
            <div className="flex items-center justify-center lg:justify-start mb-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
                <img 
                  src="/SEWA.jpg" 
                  alt="SEWA Consultancy" 
                  className="w-16 h-16 rounded-xl object-cover"
                />
              </div>
              <div className="ml-4">
                <h1 className="text-3xl font-bold text-white">SEWA</h1>
                <p className="text-blue-300">Educational Consultancy</p>
              </div>
            </div>

            {/* Main Heading */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mb-8"
            >
              <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
                Welcome to Your
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
                  Education Portal
                </span>
              </h2>
              <p className="text-xl text-blue-200 leading-relaxed">
                Access expert guidance, personalized consultations, and comprehensive educational resources
              </p>
            </motion.div>

            {/* Features Grid */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-4"
            >
              {consultancyFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                  className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:bg-white/20 transition-all duration-300"
                >
                  <div className="flex items-center space-x-3">
                    <div className="text-blue-400">
                      {feature.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-white text-sm">{feature.title}</h3>
                      <p className="text-blue-200 text-xs">{feature.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Side - Login Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 shadow-2xl"
          >
            {/* Login Type Toggle */}
            <div className="flex bg-white/10 backdrop-blur-sm rounded-xl p-1 mb-6">
              <button
                type="button"
                onClick={() => setLoginType('consultant')}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all duration-300 ${
                  loginType === 'consultant'
                    ? 'bg-blue-500 text-white shadow-lg'
                    : 'text-blue-200 hover:text-white'
                }`}
              >
                <Users className="h-4 w-4 inline mr-2" />
                Consultant
              </button>
              <button
                type="button"
                onClick={() => setLoginType('admin')}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all duration-300 ${
                  loginType === 'admin'
                    ? 'bg-purple-500 text-white shadow-lg'
                    : 'text-blue-200 hover:text-white'
                }`}
              >
                <Shield className="h-4 w-4 inline mr-2" />
                Admin
              </button>
            </div>

            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-white mb-2">
                {loginType === 'admin' ? 'Admin Access' : 'Consultant Portal'}
              </h3>
              <p className="text-blue-200">
                {loginType === 'admin' 
                  ? 'Administrative dashboard access' 
                  : 'Sign in to manage consultations'
                }
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg backdrop-blur-sm"
                >
                  {error}
                </motion.div>
              )}

              {/* Email Field */}
              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-blue-400" />
                  </div>
                  <input
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      }
                    })}
                    type="email"
                    className="block w-full pl-10 pr-3 py-3 bg-white/10 backdrop-blur-sm border border-white/30 rounded-lg text-white placeholder-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    placeholder="consultant@sewa.com"
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-400">{errors.email.message}</p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-blue-400" />
                  </div>
                  <input
                    {...register('password', {
                      required: 'Password is required',
                      minLength: {
                        value: 6,
                        message: 'Password must be at least 6 characters'
                      }
                    })}
                    type={showPassword ? 'text' : 'password'}
                    className="block w-full pl-10 pr-10 py-3 bg-white/10 backdrop-blur-sm border border-white/30 rounded-lg text-white placeholder-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-blue-400 hover:text-blue-300"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-400">{errors.password.message}</p>
                )}
              </div>

              {/* Consultancy Code (for consultants only) */}
              {loginType === 'consultant' && (
                <div>
                  <label className="block text-sm font-medium text-blue-200 mb-2">
                    Consultancy Code <span className="text-blue-400">(Optional)</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Building className="h-5 w-5 text-blue-400" />
                    </div>
                    <input
                      {...register('consultancyCode')}
                      type="text"
                      className="block w-full pl-10 pr-3 py-3 bg-white/10 backdrop-blur-sm border border-white/30 rounded-lg text-white placeholder-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                      placeholder="SEWA-CONS-001"
                    />
                  </div>
                </div>
              )}

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-white/30 rounded bg-white/10"
                  />
                  <span className="ml-2 text-sm text-blue-200">Remember me</span>
                </label>
                <Link
                  to="/consultancy-forgot-password"
                  className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                >
                  Forgot password?
                </Link>
              </div>

              {/* Submit Button */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={isLoading}
                className={`w-full py-3 px-4 rounded-lg font-bold text-white shadow-lg transition-all duration-300 flex items-center justify-center ${
                  loginType === 'admin'
                    ? 'bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700'
                    : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    Sign In to {loginType === 'admin' ? 'Admin Panel' : 'Consultancy'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </motion.button>
            </form>

            {/* Footer Links */}
            <div className="mt-6 text-center space-y-2">
              <p className="text-sm text-blue-200">
                Need access?{' '}
                <Link to="/consultancy-request-access" className="text-blue-400 hover:text-blue-300 font-medium">
                  Request Account
                </Link>
              </p>
              <p className="text-sm text-blue-300">
                <Link to="/" className="hover:text-blue-200 transition-colors">
                  ← Back to Main Site
                </Link>
              </p>
            </div>

            {/* Security Badge */}
            <div className="mt-6 flex items-center justify-center text-green-400">
              <CheckCircle className="h-4 w-4 mr-2" />
              <span className="text-xs">Secure SSL Encrypted Connection</span>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ConsultancyLoginPage;
